import { DiscordSDK } from '@discord/embedded-app-sdk';

/**
 * Lazy accessor for the Discord Embedded App SDK singleton.
 *
 * The SDK reads its Application ID from `import.meta.env.VITE_DISCORD_CLIENT_ID`
 * — wired into the build at deploy time, not bundled into the package itself,
 * so we don't ship a hardcoded ID. The dev server reads from `.env.local` (or
 * inline-set when running `npm run dev`).
 *
 * The SDK constructor reads `frame_id`, `instance_id`, and `platform` from
 * `window.location.search` and throws if any are missing — which is true any
 * time the SPA loads outside Discord's iframe (notably the `?preview=...`
 * dev harness in `dev/PreviewHarness.tsx`). Construction is deferred to the
 * first call so module evaluation — and the harness short-circuit in
 * `App.tsx` — never trip over the throw.
 *
 * Inside a real iframe the constructor still completes its IPC handshake on
 * first use; the rest of the surface (`commands.authorize`, `ready()`, etc.)
 * is unchanged.
 */
const rawClientId = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;
if (!rawClientId) {
  throw new Error(
    'VITE_DISCORD_CLIENT_ID is not set. The Activity needs the Discord Application ID at build time. ' +
    'Add it to .env.local for dev or to the deploy environment for production.',
  );
}
// Bind the validated value to a `string`-typed const so the narrowing survives
// across the `getDiscordSdk` function boundary (TS doesn't propagate flow
// narrowing into closures over module-scoped vars).
const clientId: string = rawClientId;

let sdk: DiscordSDK | null = null;
export function getDiscordSdk(): DiscordSDK {
  if (sdk) return sdk;
  sdk = new DiscordSDK(clientId);
  return sdk;
}

export const ACTIVITY_OAUTH_SCOPES = ['identify', 'guilds.members.read'] as const;
